import {
  orderIdentificationSchema,
  type OrderIdentificationFormValues,
} from "@babackapp/return-widget-components/forms/order-identification.form";
import { useSearchOrder } from "@babackapp/return-widget-components/mutations/use-search-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useAuth } from "@babackapp/auth-client/hooks";
import { seedOrderLinkToken } from "@babackapp/private-api-client/client/return-widget-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

export type OrderIdentificationMode = "normal" | "retractation";

export function useOrderIdentificationForm() {
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();
  const [searchParams] = useSearchParams();
  const bypassOrderId = searchParams.get("orderId");
  // Signed order token from the e-mailed return link. Seeded into the session
  // before the bypass lookup so it travels as the request's authorization —
  // that is what entitles the customer to this order (BAB-3042).
  const orderLinkToken = searchParams.get("bbt");
  const { data: shop } = usePublicShop(shopId!);
  const mutation = useSearchOrder();
  const { user, refresh, isLoading: isAuthLoading } = useAuth();
  const isMerchant = !!user;
  const [mode, setMode] = useState<OrderIdentificationMode>("normal");
  const hasAttemptedBypass = useRef(false);
  const [isBypassSettled, setIsBypassSettled] = useState(false);
  // Auth still initializing at mount means the init is about to refresh the token
  // itself: the bypass then has a live merchant without refreshing a second time.
  const isAuthRefreshedOnMount = useRef(isAuthLoading);

  const validationInfo = shop.preferences?.userPortal?.validationInformations ?? {
    email: true,
    trackingNumber: false,
    zipCode: false,
  };

  const schema = isMerchant
    ? orderIdentificationSchema
    : orderIdentificationSchema.extend({
        secondaryInput: z.string().min(1, "Ce champ est requis"),
      });

  const form = useForm<OrderIdentificationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { orderName: "", secondaryInput: "" },
  });

  /**
   * The merchant identity travels as the office access token, read from the cookie at
   * request time. That token lives 30 minutes and the proactive refresh skips hidden
   * tabs, so a widget left open sends a dead token. The lookup then answers
   * order-not-found: the merchant is simply not recognised. Refreshing first keeps the
   * identification the one call that always carries a live merchant.
   */
  const refreshMerchantToken = async () => {
    if (!isMerchant) return;
    await refresh().catch(() => undefined);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    await refreshMerchantToken();
    const data = await mutation.mutateAsync({
      orderName: values.orderName,
      secondaryInput: isMerchant ? undefined : values.secondaryInput,
      providerId: shop.providerId,
    });
    const base = `order/${data.orderId}`;
    navigate(mode === "retractation" ? `${base}/retractation-article-selection` : `${base}/return-requests`);
  });

  // ?orderId= bypass — skip the manual lookup form entirely, mirroring the old widget's
  // behavior of auto-navigating once the order resolves from the query param.
  // It waits for the auth initialization: the bypass runs once, and before `user` is
  // known `isMerchant` is false, so the lookup would skip the merchant token refresh
  // and go out as an anonymous caller (BBS-389). The navigation replaces the entry:
  // going back to `?orderId=` would only run the bypass again.
  useEffect(() => {
    if (!bypassOrderId || isAuthLoading || hasAttemptedBypass.current) return;
    hasAttemptedBypass.current = true;
    seedOrderLinkToken(orderLinkToken, bypassOrderId);
    (isAuthRefreshedOnMount.current ? Promise.resolve() : refreshMerchantToken())
      .then(() =>
        mutation.mutateAsync({ orderId: bypassOrderId, providerId: shop.providerId }),
      )
      .then((data) => {
        navigate(`order/${data.orderId}/return-requests`, { replace: true });
      })
      .catch(() => {
        // Fall through to the manual identification form on failure.
        setIsBypassSettled(true);
      });
  }, [bypassOrderId, isAuthLoading, orderLinkToken, shop.providerId]);

  return {
    form,
    mutation,
    onSubmit,
    logoUrl: shop.logoUrl ?? null,
    isMerchant,
    shop,
    mode,
    setMode,
    validationInfo,
    // Covers the auth wait and the token refresh too, not only the lookup: the form
    // must not flash before the bypass has had its chance.
    isBypassing: !!bypassOrderId && !isBypassSettled,
  };
}
